<template>
    <div
        :class="{
            'is--disabled': isDisabled,
            'is--active': isActive
        }"
        class="top-navigation-tab group"
    >
        <span
            v-if="!editMode && isDesktop"
            class="absolute left-2 top-1/2 -translate-y-1/2 hidden group-hover:block transition duration-200"
        >
            <i class="vc-icon-dragdrop text-secondary-actions" />
        </span>
        <div
            v-if="isActive && editMode"
            class="min-w-40"
        >
            <RedesignBaseInput
                v-model="groupTitleModel"
                :placeholder="String($t('common.setGroupTitle') || '')"
                size="mini"
                class="!text-main-xs"
            />
        </div>
        <div
            v-else
            class="top-navigation-tab__name hover:!text-primary-actions--focus"
            @click="switchTab"
        >
            {{ groupName }}
        </div>
        <template v-if="isDesktop">
            <TabsNavigationPopover
                v-if="!editMode"
                :number-of-widget-group="numberOfWidgetGroup"
                @on-remove-widget-group="onRemoveWidgetGroup"
                @on-copy-widget-group="onCopyWidgetGroup"
                @on-update-filters-widget-group="onUpdateFiltersWidgetGroup"
            />
            <span
                v-else
                class="w-4 h-4"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import { WidgetGroupList } from '@/types/dashboard'
import { computed, watch } from 'vue'
import i18n from '@/i18n'
import TabsNavigationPopover from '@/components/RedesignComponents/Popover/TabsNavigationPopover.vue'
import { useGroupNavigationInject } from '@/composables/useGroupNavigationTabs'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import set from 'lodash/set'
import { debouncedWatch } from '@vueuse/core'
import { isDesktop } from '@/composables/useBreakpoints'

/* Composable */
const { onSwitchTab, editMode, onCopyGroup, onRemoveGroup, onUpdateGroupName, onUpdateGroupFilters } = useGroupNavigationInject()

interface IProps {
    isActive?: boolean
    groupData: WidgetGroupList,
    numberOfWidgetGroup: number
}

const props = defineProps<IProps>()
const emit = defineEmits<{
    (e: 'on-copy', pl: WidgetGroupList): void
    (e: 'on-remove', pl: WidgetGroupList): void
    (e: 'on-switch'): void
    (e: 'on-update-filters', pl: WidgetGroupList): void
}>()

const groupTitleModel = computed({
    get () {
        return props.groupData?.WidgetGroupTitle
    },
    set (val) {
        set(props.groupData, 'WidgetGroupTitle', val)
    }
})
const isDisabled = computed(() => {
    return !props.isActive && editMode?.value
})
const groupName = computed(() => {
    return props.groupData.WidgetGroupTitle || i18n.t('dashboard.groupID') + ': ' + props.groupData.WidgetGroupID
})

/* Methods */
function onRemoveWidgetGroup () {
    if (onRemoveGroup) {
        onRemoveGroup(props.groupData)
    }
    emit('on-remove', props.groupData)
}

function onCopyWidgetGroup () {
    if (onCopyGroup) {
        onCopyGroup(props.groupData)
    }
    emit('on-copy', props.groupData)
}

function onUpdateFiltersWidgetGroup () {
    if (onUpdateGroupFilters) {
        onUpdateGroupFilters(props.groupData)
    }
    emit('on-update-filters', props.groupData)
}

function switchTab () {
    if (props.isActive || !onSwitchTab || isDisabled.value) {
        return
    }
    onSwitchTab(props.groupData)
}

watch(
    () => editMode?.value,
    (val) => {
        if (val && !groupTitleModel.value) {
            groupTitleModel.value = groupName.value
        }
    }
)
debouncedWatch(
    groupTitleModel,
    () => {
        if (onUpdateGroupName) {
            onUpdateGroupName(groupTitleModel.value, props.groupData.WidgetGroupID)
        }
    },
    {
        debounce: 1000
    }
)
</script>

<style lang="scss">
.top-navigation-tab {
    @apply
    h-full
    pr-3 pl-2 lg:pl-4 xl:pl-6
    py-2 lg:py-3.5
    select-none whitespace-nowrap relative
    flex items-center justify-between gap-x-3
    font-semibold cursor-pointer;
    background: var(--mainBgColor-color);
    color: var(--widgetGroupTitles-color);

    &.is--active {
        @apply text-primary hover:text-primary-actions--focus;
        &:after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            width: 100%;
            height: 4px;
            background-color: var(--primary-color);

        }

        .top-navigation-tab__name {
            @apply text-primary;
        }
    }

    &.is--disabled {
        @apply opacity-50 pointer-events-none;
    }
}
</style>
